import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import useI18n from 'hooks/useI18n'
import useNotificationCenter from 'hooks/useNotificationCenter'
import { getPortalConfig, isLitePath } from 'utils/portal'

function notificationRoute(pathname) {
    if (pathname.startsWith('/mvu-accelerator')) return getPortalConfig('accelerator').notificationsRoute
    if (isLitePath(pathname)) return getPortalConfig('essentials').notificationsRoute
    return getPortalConfig('universe').notificationsRoute
}

export default function useController() {
    const history = useHistory()
    const location = useLocation()
    const { id } = useParams()
    const { t } = useI18n()
    const readAttemptRef = useRef(null)
    const {
        notifications,
        loading,
        loaded,
        error,
        loadNotifications,
        markNotificationRead,
    } = useNotificationCenter()

    const notification = useMemo(() => notifications.find(item => `${item.id}` === `${id}`) || null, [id, notifications])

    useEffect(() => {
        if (notification?.unread && `${readAttemptRef.current}` !== `${notification.id}`) {
            readAttemptRef.current = notification.id
            markNotificationRead(notification)
        }
    }, [markNotificationRead, notification])

    const goBack = useCallback(() => {
        if (window.history.length > 1) {
            history.goBack()
            return
        }

        history.push(notificationRoute(location.pathname))
    }, [history, location.pathname])

    const goToAction = useCallback(() => {
        if (!notification?.url) return

        if (notification.url.startsWith('/')) {
            history.push(notification.url)
            return
        }

        window.open(notification.url, '_blank', 'noopener,noreferrer')
    }, [history, notification])

    const goToNotifications = useCallback(() => {
        history.push(notificationRoute(location.pathname))
    }, [history, location.pathname])

    return {
        notification,
        loading,
        loaded,
        error,
        retry: loadNotifications,
        goBack,
        goToAction,
        goToNotifications,
        t,
    }
}
